import React from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import male from "../../../../assets/male.png";
import { AgentCount } from "./chatAnalyticsService";
import { GRID_COLOR, renderTooltipCard, renderTooltipHeader, renderTooltipRow } from "./chatAnalyticsPalette";

interface ChatAgentWorkloadChartProps {
  agents: AgentCount[];
}

// "Replies they sent" is a subset of "messages in their chats", not a
// separate total — stacking "replies sent" on top of "other messages"
// (messages minus their own replies) keeps the bar's full height an honest
// total instead of double-counting the replies twice.
const ChatAgentWorkloadChart: React.FC<ChatAgentWorkloadChartProps> = ({ agents }) => {
  if (agents.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "320px" }}>
        <div className="text-center">
          <i className="ti ti-users fs-1 text-muted mb-3" />
          <p className="text-muted mb-0">No agents have handled a chat yet</p>
        </div>
      </div>
    );
  }

  const sorted = [...agents].sort((a, b) => b.total_messages - a.total_messages);
  const categories = sorted.map((a) => a.agent_name);
  const otherMessages = sorted.map((a) => Math.max(0, a.total_messages - a.total_agent_replies));
  const series = [
    { name: "Other messages", data: otherMessages },
    { name: "Replies they sent", data: sorted.map((a) => a.total_agent_replies) },
  ];
  const seriesColors = ["#c7bdfa", "#f59e0b"];

  const options: ApexOptions = {
    chart: { type: "bar", height: 300, stacked: true, toolbar: { show: false }, animations: { enabled: false } },
    colors: seriesColors,
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 5,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        barHeight: "62%",
      },
    },
    fill: { type: "gradient", gradient: { shade: "light", type: "horizontal", shadeIntensity: 0.25, opacityFrom: 1, opacityTo: 0.88 } },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    grid: { borderColor: GRID_COLOR, strokeDashArray: 4, yaxis: { lines: { show: false } } },
    xaxis: { labels: { formatter: (val: string) => Math.round(Number(val)).toString() } },
    yaxis: { labels: { style: { fontWeight: 600 } } },
    legend: { position: "bottom", fontWeight: 600, markers: { size: 7 } },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex }) => {
        const agent = sorted[dataPointIndex];
        const rows = [
          { label: "Other messages", value: otherMessages[dataPointIndex], color: seriesColors[0] },
          { label: "Replies they sent", value: agent.total_agent_replies, color: seriesColors[1] },
        ];
        return renderTooltipCard(
          renderTooltipHeader(agent.agent_name, agent.total_messages) +
            rows.map((r, i) => renderTooltipRow(r.label, r.value, r.color, i === seriesIndex)).join("") +
            renderTooltipRow("Conversations", agent.total_chats)
        );
      },
    },
    states: { hover: { filter: { type: "darken" } } },
  };

  return (
    <>
      <Chart options={options} series={series} type="bar" height={300} />
      <div className="table-responsive mt-3">
        <table className="table ca-agent-table mb-0">
          <thead>
            <tr>
              <th>Agent</th>
              <th className="text-center">Chats</th>
              <th className="text-center">Messages</th>
              <th className="text-center">Replies</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((agent) => (
              <tr key={agent.agent_name} className={agent.total_messages ? "" : "text-muted"}>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <img src={male} alt={agent.agent_name} className="ca-avatar-img" />
                    {agent.agent_name}
                  </div>
                </td>
                <td className="text-center num">{agent.total_chats}</td>
                <td className="text-center num">{agent.total_messages}</td>
                <td className="text-center num">{agent.total_agent_replies}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ChatAgentWorkloadChart;
